import { useNavigate } from 'react-router';
import type { IconName } from '../../../ui/index.js';
import { Icon } from '../../../ui/index.js';
import { Menu } from '../../../ui/index.js';
import { useAuthStore } from '../../../stores/auth.js';
import { useNotificationsList, splitNotifications } from '../hooks/useNotificationsList.js';
import { useMarkNotificationViewed } from '../hooks/useMarkNotificationViewed.js';
import { NotificationBellPanel } from './NotificationBellPanel.js';

/**
 * @aiPurpose Shared topbar notification menu for both the messages (chat) and
 *   events (non-chat) channels. Both instances reuse the single
 *   `['notifications','list']` query — one network request regardless of how
 *   many menus are mounted. The `channel` prop drives a client-side partition;
 *   no backend change is needed (#2107).
 */

export interface NotificationMenuProps {
  channel: 'messages' | 'events';
  icon: IconName;
  ariaLabel: string;
  /** button testid = `${testIdPrefix}`, badge testid = `${testIdPrefix}-badge` */
  testIdPrefix: string;
}

export function NotificationMenu({
  channel,
  icon,
  ariaLabel,
  testIdPrefix,
}: NotificationMenuProps) {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const query = useNotificationsList({ enabled: Boolean(user) });
  const markViewed = useMarkNotificationViewed();
  if (!user) return null;

  const subset = (query.data ?? []).filter((r) =>
    channel === 'messages' ? r.notificationType === 'chat' : r.notificationType !== 'chat',
  );
  const split = query.data ? splitNotifications(subset) : null;
  const unreadCount = split?.unreadAggregateCount ?? 0;
  const badgeLabel = unreadCount > 99 ? '99+' : String(unreadCount);

  return (
    <Menu
      minWidth={320}
      trigger={(triggerProps) => (
        <button
          ref={triggerProps.ref as (el: HTMLButtonElement | null) => void}
          type="button"
          aria-label={ariaLabel}
          aria-haspopup={triggerProps['aria-haspopup']}
          aria-expanded={triggerProps['aria-expanded']}
          aria-controls={triggerProps['aria-controls']}
          onClick={triggerProps.onClick}
          onKeyDown={triggerProps.onKeyDown}
          data-testid={testIdPrefix}
          style={{
            position: 'relative',
            width: 32,
            height: 32,
            borderRadius: 'var(--r-1)',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--fg-2)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon name={icon} size={18} />
          {unreadCount > 0 && (
            <span
              aria-hidden="true"
              data-testid={`${testIdPrefix}-badge`}
              style={{
                position: 'absolute',
                top: 2,
                right: 2,
                minWidth: 16,
                height: 16,
                padding: '0 4px',
                borderRadius: 8,
                background: 'var(--status-error-fg)',
                color: 'var(--fg-invert)',
                fontSize: 10,
                fontWeight: 600,
                lineHeight: '16px',
                textAlign: 'center',
              }}
            >
              {badgeLabel}
            </span>
          )}
        </button>
      )}
    >
      <NotificationBellPanel
        loading={query.isPending}
        error={query.isError ? query.error : null}
        split={split}
        onRowSelect={(row) => {
          // INVARIANT (#1068): server is authoritative — mark-viewed
          //   triggers regardless of optimistic state so a re-emit
          //   between cache and click does not leave the row stuck
          //   unread.
          if (row.unreadCount > 0) {
            markViewed.mutate({ by: 'id', id: row.id });
          }
        }}
        navigate={navigate}
      />
    </Menu>
  );
}
